// frontend/app/components/lessonPlans/LessonFinder.js
//
// "Describe the lesson you want." The box that replaced the tile grid.
//
// WHY A BOX AND NOT TILES. A grid works while a board has twelve lessons and
// stops working at fifty: a teacher who knows exactly what tomorrow's class is
// about had to recognise it in a wall of cards. Typing what the lesson is about
// is how they would say it out loud, and the list narrows as they say it.
//
// Ranking is local and instant (./lessonMatch), because the whole board's
// lesson list is already in memory. The server is asked only when the words
// find nothing, and that request is allowed to fail quietly — see `onAiMatch`.
//
// It can never be a dead end: when nothing matches it says so, in the words the
// audience uses, and leaves the sentence in the box to be edited. There is no
// state in which this component renders a void.
//
// AND WHAT A BOX CANNOT DO. Describing a lesson only works if you already
// believe it exists. `./LessonBrowseTree` is the way round that — subject, then
// year, then the lessons — and it is collapsed, because a tree rendered open is
// the tile grid again with an indent. The two never share the screen: see
// `showResults`.

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { getLessonColors } from "./lessonPlanTheme";
import { rankLessons } from "./lessonMatch";
import LessonBrowseTree from "./LessonBrowseTree";
import ResultRow from "./LessonResultRow";

/**
 * Long enough that a fast typist ranks once per word rather than once per
 * letter, short enough that the list feels attached to the keyboard.
 */
const DEBOUNCE_MS = 180;

/** Past this the dropdown stops being a shortlist and becomes the grid again. */
const RESULT_LIMIT = 8;

/** A shortlist from Saras, not a second result list underneath the first. */
const AI_LIMIT = 5;

/** A description of a lesson, not an essay. A stray paste is not a query. */
const MAX_QUERY_CHARS = 300;

/**
 * On the lessons index the box is the page, so it is capped and centred instead
 * of running the full width of the content area. A 930px-wide single-line strip
 * reads as a search bar; this reads as something you compose in, which is what
 * it is — the placeholder asks for a sentence, not a keyword.
 */
const PAGE_BOX_MAX = 680;

/**
 * The four strings that change with who is looking at the box.
 *
 * One `audience` prop rather than four string props, because these have to stay
 * coherent with each other: a caller that overrode only the placeholder would
 * leave a student reading "what the class should be able to do" in the hint
 * underneath, which is the exact bug this replaced. Keeping them side by side
 * also puts the whole voice in front of a translator at once.
 *
 * A student has no class and picks nothing for one; they are looking for
 * something to learn, so that is what the box says to them.
 */
const FINDER_COPY = {
  teacher: {
    label: "Describe the lesson you want",
    placeholder:
      "Describe the lesson you want — a topic, a concept, or what the class should be able to do…",
    emptyHint: "Try a topic, a concept, or what the class should be able to do.",
  },
  student: {
    label: "Find a lesson",
    placeholder: "Find a lesson — a topic, or something you want to learn about…",
    emptyHint: "Try a topic, or something you want to learn about.",
  },
};

const GroupHeading = ({ label, caption, colors, accent }) => (
  <View style={styles.groupHeading}>
    <Text style={[styles.groupLabel, { color: accent || colors.textMuted }]}>
      {label}
    </Text>
    {caption ? (
      <Text style={[styles.groupCaption, { color: colors.textMuted }]}>{caption}</Text>
    ) : null}
  </View>
);

const LessonFinder = ({
  lessons = [],
  progressByLesson = {},
  isDarkMode = false,
  onOpenLesson,
  onEditLesson,
  // `(query) => Promise<{ matches: [{ lesson_plan_id, score, reason }], source }>`,
  // where `source` is "openai" or "heuristic". A bare array is accepted too and
  // read as an unknown source, which attributes nothing to Saras.
  //
  // Absent, the box is local ranking only — which is a complete feature on its
  // own, so a caller that cannot reach the API is not a broken caller.
  onAiMatch = null,
  // "teacher" | "student". Only wording changes — the box's and the browse
  // tree's; everything else about finding a lesson is the same job on both
  // dashboards.
  audience = "teacher",
  // "section" | "page". Proportions only: a dashboard column gets the compact
  // strip and a browse tree whose lesson list is height-capped, the lessons
  // index gets the composition and a tree that runs as long as it likes. What
  // the finder does is identical in both.
  layout = "section",
  t = (value) => value,
}) => {
  const translate = t;
  const copy = FINDER_COPY[audience] || FINDER_COPY.teacher;
  const isPage = layout === "page";
  const colors = getLessonColors(isDarkMode);

  const [query, setQuery] = useState("");
  const [settled, setSettled] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [focused, setFocused] = useState(false);
  // Escape hides the list without throwing away what was typed; the next
  // keystroke brings it back.
  const [hidden, setHidden] = useState(false);
  // Owned here rather than by the tree, because the tree and the result list
  // are two answers to the same question and only one may be on screen.
  const [browsing, setBrowsing] = useState(false);
  const [ai, setAi] = useState({
    query: "",
    asking: false,
    matches: [],
    source: null,
  });

  useEffect(() => {
    const timer = setTimeout(() => setSettled(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const local = useMemo(
    () => (settled ? rankLessons(lessons, settled, { limit: RESULT_LIMIT }) : []),
    [lessons, settled]
  );

  const localEmpty = Boolean(settled) && local.length === 0;

  // Held in a ref, not a dependency: callers build this handler inline, and an
  // identity that changes every render would re-ask on every render.
  const askRef = useRef(onAiMatch);
  askRef.current = onAiMatch;
  const canAsk = typeof onAiMatch === "function";

  useEffect(() => {
    if (!localEmpty || !canAsk) {
      setAi({ query: "", asking: false, matches: [], source: null });
      return undefined;
    }

    // `live` is what stops an answer to a sentence the teacher has already
    // rewritten from appearing underneath the new one.
    let live = true;
    setAi({ query: settled, asking: true, matches: [], source: null });

    Promise.resolve()
      .then(() => askRef.current(settled))
      .then((result) => {
        if (!live) return;
        const payload = Array.isArray(result) ? { matches: result } : result || {};
        setAi({
          query: settled,
          asking: false,
          matches: Array.isArray(payload.matches) ? payload.matches : [],
          source: payload.source || null,
        });
      })
      .catch(() => {
        // Deliberately silent. The teacher asked for lessons, not for a report
        // on a suggestion service; they get the same "nothing matched" line
        // they would get if Saras had simply had no idea.
        if (!live) return;
        setAi({ query: settled, asking: false, matches: [], source: null });
      });

    return () => {
      live = false;
    };
  }, [canAsk, localEmpty, settled]);

  // Server matches resolved against the list the client can actually see. An id
  // the teacher has no access to is dropped rather than rendered as a row that
  // opens nothing.
  const suggested = useMemo(() => {
    if (!localEmpty || ai.query !== settled) return [];
    const byId = new Map(lessons.map((lesson) => [String(lesson.id), lesson]));
    return ai.matches
      .map((match) => ({
        lesson: byId.get(String(match?.lesson_plan_id)),
        reason: match?.reason || null,
      }))
      .filter((row) => Boolean(row.lesson))
      .slice(0, AI_LIMIT);
  }, [ai, lessons, localEmpty, settled]);

  // One flat list behind the two visual groups, so arrow keys walk everything
  // on screen in the order it is read.
  const rows = useMemo(() => {
    if (!settled) return [];
    return [
      ...local.map((hit) => ({ ...hit, reason: null })),
      ...suggested.map((row) => ({ ...row, terms: [], reason: row.reason })),
    ];
  }, [local, settled, suggested]);

  // A new result set means the old highlight points at a different lesson.
  useEffect(() => {
    setActiveIndex(0);
  }, [settled, rows.length]);

  const open = (lesson) => {
    if (lesson && onOpenLesson) onOpenLesson(lesson);
  };

  // Nothing at all until a teacher types. The box is the whole surface: a list
  // sitting under it permanently turns "describe the lesson you want" back into
  // the tile grid it replaced, just rendered as rows.
  //
  // And nothing while the tree is open. Results and a tree are two answers to
  // one question; stacked, they would push each other around the page and the
  // teacher would have to work out which list they were looking at. The tree
  // yields to the box, never the other way round — typing closes it (see
  // `onChangeText`), so the box can always take the space back.
  const showResults = !hidden && !browsing && Boolean(settled);

  const onKeyPress = (event) => {
    const key = event?.nativeEvent?.key;
    if (!key) return;
    const shift = event?.nativeEvent?.shiftKey ?? event?.shiftKey ?? false;
    const stop = () => {
      if (typeof event.preventDefault === "function") event.preventDefault();
    };

    // One meaning, whatever is open: put away whatever is under the box. The
    // tree first, because it is the thing that was opened deliberately.
    if (key === "Escape") {
      stop();
      if (browsing) setBrowsing(false);
      else setHidden(true);
      return;
    }
    // Shift+Enter is a newline — this is a multi-line box, and a teacher
    // writing two sentences about a lesson should be able to.
    if (key === "Enter" && !shift) {
      if (showResults && rows.length > 0) {
        stop();
        open(rows[Math.min(activeIndex, rows.length - 1)].lesson);
      }
      return;
    }
    if (key === "ArrowDown" || key === "ArrowUp") {
      // Inert while the tree is open: there is no highlighted row to walk, and
      // an arrow key that silently revealed the list behind the tree would put
      // both on screen at once.
      if (browsing || rows.length === 0) return;
      stop();
      setHidden(false);
      setActiveIndex((index) => {
        const next = key === "ArrowDown" ? index + 1 : index - 1;
        // Wraps, so holding one arrow key reaches every row without the teacher
        // having to know which end of the list they are at.
        return (next + rows.length) % rows.length;
      });
    }
  };

  // Attribution, not decoration. The server answers with "openai" or
  // "heuristic", and a heuristic `reason` was written by a deterministic ranker
  // — calling that Saras would claim a judgement nothing made. Same rule the
  // Loop applies to `ai_feedback.source` on a written answer.
  const bySaras = ai.source === "openai";

  return (
    <View testID="lesson-finder">
      <View
        testID="lesson-finder-box"
        style={[
          styles.box,
          isPage && styles.boxPage,
          {
            backgroundColor: colors.cardBg,
            // `border` is a divider weight and disappears at the edge of a card
            // on an almost-white page. On the index the box wears full Saras
            // blue from the start — that weight is what makes it read as the
            // thing you came for, rather than a field you have to find.
            borderColor: isPage || focused ? colors.accentPrimary : colors.border,
            // Focus cannot darken an edge that is already at full strength, so
            // it lights the box instead: the neutral depth shadow becomes a
            // glow in the border's own hue. Nothing resizes, so the box does
            // not shift under the cursor that just clicked it.
            shadowColor: focused ? colors.focusRing : colors.shadow,
            shadowOpacity: focused ? 1 : colors.shadowOpacity,
          },
        ]}
      >
        <MaterialCommunityIcons
          name="text-search"
          size={18}
          color={focused ? colors.accentPrimary : colors.textMuted}
          style={styles.boxIcon}
        />
        <TextInput
          testID="lesson-finder-input"
          value={query}
          onChangeText={(value) => {
            setQuery(value);
            setHidden(false);
            // The box is the primary path, so the moment it is used it takes
            // the space back. Nothing typed is thrown away — closing the tree
            // reveals the results for the words still sitting in the box.
            setBrowsing(false);
          }}
          onKeyPress={onKeyPress}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          multiline
          maxLength={MAX_QUERY_CHARS}
          blurOnSubmit={false}
          accessibilityLabel={translate(copy.label)}
          placeholder={translate(copy.placeholder)}
          placeholderTextColor={colors.textMuted}
          style={[styles.input, isPage && styles.inputPage, { color: colors.textPrimary }]}
        />
        {query ? (
          <TouchableOpacity
            onPress={() => {
              setQuery("");
              setHidden(false);
              setBrowsing(false);
            }}
            accessibilityRole="button"
            accessibilityLabel={translate("Clear")}
            style={styles.clear}
          >
            <MaterialCommunityIcons
              name="close-circle"
              size={16}
              color={colors.textMuted}
            />
          </TouchableOpacity>
        ) : null}
      </View>

      {/* The way in for a teacher who does not already know the lesson exists.
          Under the box, quiet, and closed — so the resting state of the page is
          still one question and one box. */}
      <LessonBrowseTree
        lessons={lessons}
        progressByLesson={progressByLesson}
        colors={colors}
        isDarkMode={isDarkMode}
        open={browsing}
        onToggle={() => setBrowsing((value) => !value)}
        // The same handler a search result opens with, so a lesson behaves
        // identically however it was arrived at.
        onOpenLesson={open}
        onEditLesson={onEditLesson}
        audience={audience}
        layout={layout}
        t={t}
      />

      {showResults ? (
        // Not a scroller of its own: the result count is already capped, and a
        // panel with its own scrollbar would hide the row the arrow keys had
        // just highlighted. The page scrolls instead.
        <View
          testID="lesson-finder-results"
          style={[styles.results, isPage && styles.resultsPage]}
        >
          {rows.length > 0 ? (
            <GroupHeading colors={colors} label={translate("Matching lessons")} />
          ) : null}

          {local.map(
            (hit, index) => (
              <ResultRow
                key={hit.lesson.id}
                lesson={hit.lesson}
                progress={progressByLesson[hit.lesson.id]}
                terms={hit.terms}
                active={index === activeIndex}
                colors={colors}
                isDarkMode={isDarkMode}
                onOpen={open}
                onEdit={onEditLesson}
                t={t}
              />
            )
          )}

          {ai.asking ? (
            <View testID="lesson-finder-asking" style={styles.state}>
              <ActivityIndicator size="small" color={colors.accentPrimary} />
              <Text style={[styles.stateText, { color: colors.textMuted }]}>
                {translate("Asking Saras…")}
              </Text>
            </View>
          ) : null}

          {suggested.length > 0 ? (
            <View
              testID="lesson-finder-ai-group"
              style={[
                styles.aiGroup,
                { backgroundColor: colors.aiCardBg, borderColor: colors.aiCardBorder },
              ]}
            >
              <GroupHeading
                colors={colors}
                accent={bySaras ? colors.accentPrimary : colors.textSecondary}
                label={
                  bySaras
                    ? translate("Saras suggests")
                    : translate("Closest in the library")
                }
                caption={
                  bySaras
                    ? translate(
                        "Your words did not appear in any lesson, so Saras picked from the library."
                      )
                    : translate(
                        "Your words did not appear in any lesson. These are the closest the library ranks — nobody read your sentence."
                      )
                }
              />
              {suggested.map((row, index) => (
                <ResultRow
                  key={row.lesson.id}
                  lesson={row.lesson}
                  progress={progressByLesson[row.lesson.id]}
                  reason={row.reason}
                  reasonIsAi={bySaras}
                  active={local.length + index === activeIndex}
                  colors={colors}
                  isDarkMode={isDarkMode}
                  onOpen={open}
                  onEdit={onEditLesson}
                  t={t}
                />
              ))}
            </View>
          ) : null}

          {settled && !ai.asking && rows.length === 0 ? (
            <View
              testID="lesson-finder-empty"
              style={[styles.empty, { borderColor: colors.border }]}
            >
              <MaterialCommunityIcons
                name="text-search"
                size={20}
                color={colors.textMuted}
              />
              <Text style={[styles.emptyTitle, { color: colors.textPrimary }]}>
                {translate("No lessons match those words.")}
              </Text>
              <Text style={[styles.emptyHint, { color: colors.textMuted }]}>
                {translate(copy.emptyHint)}
              </Text>
            </View>
          ) : null}
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  box: {
    flexDirection: "row",
    alignItems: "flex-start",
    borderWidth: 1,
    borderRadius: 14,
    paddingHorizontal: 14,
    paddingVertical: 10,
    gap: 10,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 3 },
    elevation: 2,
  },
  boxIcon: { marginTop: 4 },
  // Room for the three lines the placeholder itself runs to, so the box is not
  // asking for a sentence in the space of a word.
  boxPage: {
    width: "100%",
    maxWidth: PAGE_BOX_MAX,
    alignSelf: "center",
    borderRadius: 18,
    paddingHorizontal: 18,
    paddingVertical: 16,
    // Lifted further than the inline box. This one is alone in the middle of an
    // otherwise empty page, so it has no neighbours to give it an edge — the
    // shadow is the only thing separating a white card from a near-white page.
    // Wide and soft rather than tight and dark: a small offset with a large
    // blur reads as height, a large offset reads as a sticker.
    shadowRadius: 28,
    shadowOffset: { width: 0, height: 8 },
    elevation: 6,
  },
  input: {
    flex: 1,
    minHeight: 44,
    fontSize: 14,
    lineHeight: 20,
    // Web draws its own focus ring on top of the box's border.
    outlineStyle: "none",
    textAlignVertical: "top",
  },
  inputPage: { minHeight: 88, fontSize: 15, lineHeight: 23 },
  clear: { marginTop: 4, padding: 2 },

  results: { marginTop: 12, gap: 10 },
  // Results line up under the box instead of running wider than the thing that
  // produced them.
  resultsPage: { width: "100%", maxWidth: PAGE_BOX_MAX, alignSelf: "center", marginTop: 18 },

  groupHeading: { marginBottom: 2 },
  groupLabel: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.8,
    textTransform: "uppercase",
  },
  groupCaption: { fontSize: 11, marginTop: 2 },

  aiGroup: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 10,
    gap: 10,
  },

  state: { flexDirection: "row", alignItems: "center", gap: 8, paddingVertical: 10 },
  stateText: { fontSize: 13, marginLeft: 8 },

  empty: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 18,
    alignItems: "center",
    gap: 6,
  },
  emptyTitle: { fontSize: 14, fontWeight: "700", marginTop: 4 },
  emptyHint: { fontSize: 12, textAlign: "center" },
});

export default LessonFinder;
